// Container karty klienta w Panelu Handlowca

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';

import type { IResponse as IClientResponse } from 'api/endpoints/useGetClient';
import { useGetFinancialData } from 'api';

import TraderClientData from 'components/containers/TraderClientCardContainer/TraderClientData';
import TraderClientDataHeader from 'components/containers/TraderClientCardContainer/TraderClientDataHeader';
import TraderClientFinanse from 'components/containers/TraderClientCardContainer/TraderClientFinanse';
import { WiseChart, WiseTable, WiseTabs } from 'components/wise';
import type { IWiseTableData } from 'components/wise/types';

import {
  getTraderClientCardChartParameters,
  traderClientCardChartLoadDataParams
} from './traderClientCardChartConfig';
import {
  getTraderClientCardSalesTableParameters,
  getTraderClientCardSalesTableColumns,
  getTraderClientCardSalesTableActions,
  traderClientCardSalesTableLoadDataParams
} from './traderClientCardSalesTableConfig';
import {
  getTraderClientCardTasksTableParameters,
  getTraderClientCardTasksTableColumns,
  getTraderClientCardTasksTableActions,
  traderClientCardTasksTableLoadDataParams
} from './traderClientCardTasksTableConfig';
import {
  getTraderClientCardCartsTableParameters,
  getTraderClientCardCartsTableColumns,
  getTraderClientCardCartsTableActions,
  traderClientCardCartsTableLoadDataParams
} from './traderClientCardCartsTableConfig';
import {
  getTraderClientCardDocumentsTableParameters,
  getTraderClientCardDocumentsTableColumns,
  getTraderClientCardDocumentsTableActions,
  traderClientCardDocumentsTableLoadDataParams
} from './traderClientCardDocumentsTableConfig';
import {
  getTraderClientCardShoppingListsTableParameters,
  getTraderClientCardShoppingListsTableColumns,
  getTraderClientCardShoppingListsTableActions,
  traderClientCardShoppingListsTableLoadDataParams
} from './traderClientCardShoppingListsTableConfig';

import styles from 'theme/components/containers/TraderClientCardContainer/TraderClientCardContainer.module.scss';

const CARTS_TABLE_SYMBOL = 'traderClientCardCartsTable';
const DOCUMENTS_TABLE_SYMBOL = 'traderClientCardDocumentsTable';
const SALES_TABLE_SYMBOL = 'traderClientCardSalesTable';
const SHOPPING_LISTS_TABLE_SYMBOL = 'traderClientCardShoppingListsTable';

interface ITraderClientCardContainerProps {
  clientId: number;
  client?: IClientResponse | null;
  isClientLoading?: boolean;
}

const TraderClientCardContainer: React.FC<ITraderClientCardContainerProps> = ({
  clientId,
  client,
  isClientLoading = false
}) => {
  const { t } = useTranslation();
  const { data: financialData, isLoading: isFinancialDataLoading } = useGetFinancialData(clientId);

  const [tablesData, setTablesData] = useState<Record<string, IWiseTableData>>({});

  useEffect(() => {
    setTablesData({});
  }, [clientId]);

  const handleTableDataLoad = useCallback((data: IWiseTableData, componentSymbol: string) => {
    setTablesData((prev) => ({ ...prev, [componentSymbol]: data }));
  }, []);

  const cartsSummary = useMemo(() => {
    const data = tablesData[CARTS_TABLE_SYMBOL];
    const count = data?.total_count ?? 0;
    return { count };
  }, [tablesData]);

  const salesSummary = useMemo(() => {
    const data = tablesData[SALES_TABLE_SYMBOL];
    const count = data?.total_count ?? 0;
    return { count };
  }, [tablesData]);

  const documentsSummary = useMemo(() => {
    const data = tablesData[DOCUMENTS_TABLE_SYMBOL];
    const count = data?.total_count ?? 0;
    return { count };
  }, [tablesData]);

  const shoppingListsSummary = useMemo(() => {
    const data = tablesData[SHOPPING_LISTS_TABLE_SYMBOL];
    const count = data?.total_count ?? 0;
    return { count };
  }, [tablesData]);

  const getDataTile = () => {
    return(
      clientId > 0 ? (
        <div className={styles.clientDataWrapper}>
          <TraderClientDataHeader client={client} isLoading={isClientLoading} />
          <Grid container columnSpacing="24px">
            <Grid item xs={12} md={8}>
              <TraderClientData client={client} isLoading={isClientLoading} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TraderClientFinanse client={client} data={financialData} isLoading={isFinancialDataLoading || isClientLoading} />
            </Grid>
          </Grid>
        </div>
      ) : (
        <p className={styles.placeholder}>{t('Brak wybranego klienta')}</p>
      )
    )
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderClientCardContainer'
      )}>
      {getDataTile()}

      <WiseChart
        label={t('Zakupy')}
        parameters={getTraderClientCardChartParameters(t)}
        loadDataParams={traderClientCardChartLoadDataParams}
        requestParams={{ clientId }}
        chartType="bar"
      />

      <Grid container spacing={3} marginBottom={3}>
        <Grid item xs={12} md={7}>
          <WiseTabs
            label={t('Sprzedaż rozpoczęta')}
            tabs={[
              {
                label: t('Koszyki'),
                route: `/trader/clients/${clientId}/carts`,
                subtitle: t('Ilość: {{count}}, Wartość: {{value}}', {
                  count: cartsSummary.count,
                  value: 0
                }),
                children: (
                  <WiseTable
                    label={t('Koszyki')}
                    componentSymbol={CARTS_TABLE_SYMBOL}
                    parameters={getTraderClientCardCartsTableParameters()}
                    columns={getTraderClientCardCartsTableColumns(t)}
                    actions={getTraderClientCardCartsTableActions()}
                    loadDataParams={traderClientCardCartsTableLoadDataParams}
                    requestParams={{ clientId }}
                    defaultLimit={5}
                    isCondensed
                    showPlainTable
                    rowKey="id"
                    onDataLoad={handleTableDataLoad}
                  />
                )
              },
              {
                label: t('Oferty otwarte'),
                route: `/trader/clients/${clientId}/offers`,
                subtitle: t('Ilość: {{count}}, Wartość: {{value}}', {
                  count: salesSummary.count,
                  value: 0
                }),
                children: (
                  <WiseTable
                    label={t('Oferty otwarte')}
                    componentSymbol={SALES_TABLE_SYMBOL}
                    parameters={getTraderClientCardSalesTableParameters()}
                    columns={getTraderClientCardSalesTableColumns(t)}
                    actions={getTraderClientCardSalesTableActions()}
                    loadDataParams={traderClientCardSalesTableLoadDataParams}
                    requestParams={{ clientId }}
                    defaultLimit={5}
                    isCondensed
                    showPlainTable
                    rowKey="id"
                    onDataLoad={handleTableDataLoad}
                  />
                )
              }
            ]}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <WiseTable
            label={t('Zadania')}
            componentSymbol="traderClientCardTasksTable"
            parameters={getTraderClientCardTasksTableParameters()}
            columns={getTraderClientCardTasksTableColumns(t)}
            actions={getTraderClientCardTasksTableActions(t)}
            loadDataParams={traderClientCardTasksTableLoadDataParams}
            requestParams={{ clientId }}
            defaultLimit={5}
            isCondensed
            rowKey="id"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <WiseTabs
            label={t('Dokumenty klienta')}
            tabs={[
              {
                label: t('Dokumenty finansowe'),
                route: `/trader/clients/${clientId}/documents`,
                subtitle: t('Ilość: {{count}}, Wartość: {{value}}', {
                  count: documentsSummary.count,
                  value: 0
                }),
                children: (
                  <WiseTable
                    label={t('Dokumenty finansowe')}
                    componentSymbol={DOCUMENTS_TABLE_SYMBOL}
                    parameters={getTraderClientCardDocumentsTableParameters()}
                    columns={getTraderClientCardDocumentsTableColumns(t)}
                    actions={getTraderClientCardDocumentsTableActions()}
                    loadDataParams={traderClientCardDocumentsTableLoadDataParams}
                    requestParams={{ clientId }}
                    defaultLimit={5}
                    isCondensed
                    showPlainTable
                    rowKey="id"
                    onDataLoad={handleTableDataLoad}
                  />
                )
              },
              {
                label: t('Listy zakupowe'),
                route: `/trader/clients/${clientId}/shopping-lists`,
                subtitle: t('Ilość: {{count}}, Wartość: {{value}}', {
                  count: shoppingListsSummary.count,
                  value: 0
                }),
                children: (
                  <WiseTable
                    label={t('Listy zakupowe')}
                    componentSymbol={SHOPPING_LISTS_TABLE_SYMBOL}
                    parameters={getTraderClientCardShoppingListsTableParameters()}
                    columns={getTraderClientCardShoppingListsTableColumns(t)}
                    actions={getTraderClientCardShoppingListsTableActions()}
                    loadDataParams={traderClientCardShoppingListsTableLoadDataParams}
                    requestParams={{ clientId }}
                    defaultLimit={5}
                    isCondensed
                    showPlainTable
                    rowKey="id"
                    onDataLoad={handleTableDataLoad}
                  />
                )
              }
            ]}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default TraderClientCardContainer;
